<template>
  <div>
    <MainHeader title="Plaid Agreement">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'Legal' })"
        />
      </template>
    </MainHeader>

    <div class="view legal-doc-view">
      <div class="wrapper-card legal-doc-card">
        <div class="legal-doc-header">
          <h1 class="legal-doc-title">
            Plaid End User Privacy Policy and Agreement
          </h1>
          <div class="legal-doc-dates">
            <span class="legal-date-badge">Effective: April 9, 2025</span>
            <span class="legal-date-badge">Updated: May 19, 2025</span>
          </div>
        </div>

        <p class="legal-body">
          At Lucky Friday, we use Plaid Inc. ("Plaid") to securely connect your
          financial accounts and provide financial data services. By using our
          app and connecting your financial accounts, you acknowledge and agree
          to the following:
        </p>

        <div class="legal-section">
          <h3 class="legal-section-title">1. Use of Plaid Services</h3>
          <p class="legal-body">
            When you choose to link a financial account in Lucky Friday, your
            data is shared with Plaid, a third-party service provider. Plaid
            allows us to access your:
          </p>
          <ul class="legal-list">
            <li>Account and routing numbers</li>
            <li>Account balances</li>
            <li>Transactions and spending patterns</li>
            <li>Account details (such as account type and institution name)</li>
          </ul>
          <p class="legal-body">
            This information helps us deliver Lucky Friday's budgeting and net
            worth tracking features.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">2. How Your Information Is Shared</h3>
          <p class="legal-body">
            When you connect your financial accounts, you grant Plaid the right
            to access and transmit your data to Lucky Friday. We do not store
            your banking credentials, and your information is protected using
            modern encryption and industry-standard security practices.
          </p>
          <p class="legal-body">
            For details on how Plaid uses your data, please review their
            <a href="https://plaid.com/legal" target="_blank" class="legal-link"
              >Privacy Policy</a
            >.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">3. Your Consent</h3>
          <p class="legal-body">
            By linking a financial account through Lucky Friday, you:
          </p>
          <ul class="legal-list">
            <li>
              Authorize Plaid to retrieve your financial data on your behalf
            </li>
            <li>
              Agree to the collection, use, and transfer of your data as
              described above
            </li>
            <li>Accept Plaid's End User Privacy Policy</li>
          </ul>
          <p class="legal-body">
            You may disconnect your financial accounts at any time from within
            the Lucky Friday app, and we will no longer access updated data
            through Plaid once the connection is severed.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">4. Data Security</h3>
          <p class="legal-body">
            Lucky Friday and Plaid are committed to keeping your information
            secure. We implement technical, administrative, and physical
            safeguards to protect your data from unauthorized access.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '../../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'PlaidEndUser',
  components: { MainHeader },
  setup() {
    const router = useRouter();
    return { router };
  },
};
</script>

<style scoped>
.legal-doc-view {
  max-width: 600px;
  margin: 0 auto;
}
.legal-doc-card {
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
  padding: 1.5rem;
}
.legal-doc-header {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1.5px solid #eeedfe;
}
.dark-theme .legal-doc-header {
  border-bottom-color: #2a3a4e;
}
.legal-doc-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.6rem;
  letter-spacing: -0.01em;
  line-height: 1.3;
}
.dark-theme .legal-doc-title {
  color: white;
}
.legal-doc-dates {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.legal-date-badge {
  font-size: 0.7rem;
  font-weight: 600;
  background: var(--purple-50);
  color: var(--color-accent-purple);
  padding: 2px 10px;
  border-radius: 20px;
}
.dark-theme .legal-date-badge {
  background: #1e1a40;
}
.legal-section {
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f0f0f0;
}
.dark-theme .legal-section {
  border-bottom-color: #2a3a4e;
}
.legal-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
.legal-section-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--color-accent-purple);
  margin: 0 0 0.6rem;
}
.dark-theme .legal-section-title {
  color: var(--color-accent-teal);
}
.legal-body {
  font-size: 0.82rem;
  color: #515a65;
  line-height: 1.65;
  margin: 0 0 0.6rem;
}
.dark-theme .legal-body {
  color: #d4d2d2;
}
.legal-list {
  font-size: 0.82rem;
  color: #515a65;
  line-height: 1.65;
  padding-left: 1.25rem;
  margin: 0.4rem 0;
}
.dark-theme .legal-list {
  color: #d4d2d2;
}
.legal-list li {
  margin-bottom: 0.5rem;
}
.legal-list li:last-child {
  margin-bottom: 0;
}
.legal-link {
  color: var(--color-accent-purple);
  font-weight: 500;
  text-decoration: none;
}
.legal-link:hover {
  text-decoration: underline;
}
.dark-theme .legal-link {
  color: var(--color-accent-teal);
}
</style>

<!-- <template>
  <div>
    <MainHeader title="Plaid Agreement">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'Legal' })"
        />
      </template>
    </MainHeader>

    <div class="view support-page">
      <div class="wrapper-card closing-text ps-2 pe-2 small-grey-text">
        <div>
          <h3>Plaid End User Privacy Policy and Agreement</h3>
          <h4>Effective Date: April 9, 2025</h4>
          <h4>Last Updated: May 19, 2025</h4>

          <p>
            At Lucky Friday, we use Plaid Inc. (“Plaid”) to securely connect
            your financial accounts and provide financial data services. By
            using our app and connecting your financial accounts, you
            acknowledge and agree to the following:
          </p>

          <h4>1. Use of Plaid Services</h4>
          <p>
            When you choose to link a financial account in Lucky Friday, your
            data is shared with Plaid, a third-party service provider. Plaid
            allows us to access your:
          </p>
          <ul>
            <li>Account and routing numbers</li>
            <li>Account balances</li>
            <li>Transactions and spending patterns</li>
            <li>Account details (such as account type and institution name)</li>
          </ul>
          <p>
            This information helps us deliver Lucky Friday's budgeting and net
            worth tracking features.
          </p>

          <h4>2. How Your Information Is Shared</h4>
          <p>
            When you connect your financial accounts, you grant Plaid the right
            to access and transmit your data to Lucky Friday. We do not store
            your banking credentials, and your information is protected using
            modern encryption and industry-standard security practices.
          </p>
          <p>
            For details on how Plaid uses your data, please review their
            <a href="https://plaid.com/legal" target="_blank">Privacy Policy</a
            >.
          </p>

          <h4>3. Your Consent</h4>
          <p>By linking a financial account through Lucky Friday, you:</p>
          <ul>
            <li>
              Authorize Plaid to retrieve your financial data on your behalf
            </li>
            <li>
              Agree to the collection, use, and transfer of your data as
              described above
            </li>
            <li>Accept Plaid’s End User Privacy Policy</li>
          </ul>
          <p>
            You may disconnect your financial accounts at any time from within
            the Lucky Friday app, and we will no longer access updated data
            through Plaid once the connection is severed.
          </p>

          <h4>4. Data Security</h4>
          <p>
            Lucky Friday and Plaid are committed to keeping your information
            secure. We implement technical, administrative, and physical
            safeguards to protect your data from unauthorized access.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '../../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'User-Support',
  components: {
    MainHeader,
  },
  setup() {
    const router = useRouter();

    return {
      router,
    };
  },
};
</script>
<style scoped>
a {
  color: black;
}
</style> -->
